import React from 'react'
import { ButtonGroup, Card, Col, Row, Tab, Tabs } from 'react-bootstrap'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import { DataSuspense, HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { buildGraphqlQuery } from '@ztechapps/common.utils'
import { OrderStatus } from '@ztechapps/order.common'
import { LocalDate } from '@ztechapps/webui.date'
import { OrderPaymentStatusBadge } from '@ztechapps/order.webui'
import { OrderStatusEnum } from '@ztechapps/common.contract'
import { CommunicationTab } from './CommunicationTab'
import { useContext } from './Context'
import { OrderEmptyContent } from './OrderEmptyContent'
import { BillingDetails } from './BillingDetails'
import { OrderItems } from './OrderItems'
export const queries = buildGraphqlQuery('Order', `_id`)

export const OrderContainer = () => {
  const context = useContext()
  const { document, headerContent, canEdit, error, loading, showChat, components, shippingDate } = context
  const { _id, status, code, createdAt, by, shipping, paymentStatus, paymentBalance, payments } = document ?? {}

  const hasValidTracking =
    [OrderStatusEnum.Shipped, OrderStatusEnum.InTransit, OrderStatusEnum.Delivered].includes(status) &&
    !!shipping?.trackingNumber

  return (
    <Card>
      <DataSuspense emptyComponent={<OrderEmptyContent />} loading={loading} error={error} isEmpty={isEmpty(document)}>
        <Card.Header>
          <Row className="g-3">
            <Col lg={6}>
              <ShowIf condition={!!headerContent}>
                <h4>{headerContent}</h4>
              </ShowIf>
            </Col>
            <Col lg={6}>
              <ButtonGroup>
                {components.PrintButton(context)}
                {components.CancelButton(context)}
                {components.CheckoutButton(context)}
              </ButtonGroup>
            </Col>

            <Col lg={6}>
              <HorizontalStack gap={2}>
                <div>
                  <strong className="fw-bold">Order#:</strong> {code}
                </div>

                <OrderPaymentStatusBadge
                  paymentStatus={paymentStatus}
                  balance={paymentBalance?.balance}
                  payments={payments}
                />
              </HorizontalStack>
            </Col>
            <Col lg={6}>
              <strong className="fw-bold">Order Date:</strong> <LocalDate value={createdAt} />
            </Col>
            <Col lg={6}>
              <ShowIf condition={hasValidTracking}>
                <strong className="d-inline-block">Tracking #:</strong>
                <span>
                  {map(shipping?.packages, (item, index) => {
                    const url = item?.trackingUrl

                    return (
                      <div className={'mb-1'} key={url ?? index}>
                        <a href={url} target={'_blank'} rel="noreferrer">
                          {shipping.rate.serviceType} - {item?.tracking_number}
                        </a>
                      </div>
                    )
                  })}
                </span>
              </ShowIf>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col lg={6}>
              <BillingDetails
                customerName={by?.profile?.name}
                companyName={by?.company?.name}
                customerId={by?._id}
                canEdit={canEdit}
                orderId={_id}
                addressType="billing"
                label="Bill To"
                initialValues={document}
              />
            </Col>
            <Col lg={6}>
              <BillingDetails
                customerName={by?.profile?.name}
                companyName={by?.company?.name}
                customerId={by?._id}
                canEdit={canEdit}
                orderId={_id}
                addressType="shipping"
                label="Ship To"
                initialValues={document}
              />
              <ShowIf condition={status !== OrderStatus.inTransit?.value && status !== OrderStatus.delivered?.value}>
                <strong className="fw-bold">Est. </strong>
              </ShowIf>
              <strong className="fw-bold">Shipping date: </strong> <LocalDate value={shippingDate} />
            </Col>
          </Row>

          <div className="py-3">
            <Tabs variant="underline" defaultActiveKey={'summary'} id="order-tab">
              <Tab eventKey={'summary'} title={'Summary'} mountOnEnter>
                <div className="py-2">
                  <OrderItems />
                </div>
              </Tab>
              {showChat && (
                <Tab eventKey={'communication'} title={'Messages'} mountOnEnter>
                  <div className="py-2">
                    <CommunicationTab />
                  </div>
                </Tab>
              )}
            </Tabs>
          </div>
        </Card.Body>
      </DataSuspense>
    </Card>
  )
}
